import { useEffect } from "react";

import { MagnifyingGlassIcon } from "@vericus/cadmus-icons";
import {
  Combobox,
  Command,
  DatePickerInput,
  type DatePickerValue,
  type DatesRangeValue,
  DropdownMenu,
  Input,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  InstructionType,
  IPastAssessmentFilters,
  reduxDateDeserialiser,
  SerializedDateRangePickerValue,
  updatePastAssessmentsFilter,
} from "@/data/templates";
import { selectPastAssessmentFilters } from "@/data/templates/selectors";
import {
  AssessmentType,
  SubjectFragment,
  SubjectGroup,
  TaskFormat,
} from "@/generated/graphql";
import { Counter } from "@/ui/shared/Counter";
import { getLocalDate } from "@/utils/datetime/transform-date";

import * as styles from "./past-assessments-filters.css";

export interface PastAssessmentsFiltersProps {
  /**
   * Whether the exams feature is enabled.
   */
  areExamsEnabled: boolean;
  /**
   * The subjects that the user is able to select for filtering
   */
  subjects: SubjectFragment[];
  /**
   * The subject groups that the user is able to select for filtering. Empty
   * when none of the listed assessments' subjects belong to a group, in which
   * case the Subject group dropdown is hidden.
   */
  subjectGroups: Pick<SubjectGroup, "id" | "name">[];
  /**
   * Whether the destination assessment is a group assessment. Group
   * destinations hide the assessment-type ("Type") dropdown (and its date-range
   * exam hint) and show the Instruction-type control.
   */
  isGroupAssessment: boolean;
  /**
   * Whether the destination task is multi-format. When true, sources are
   * constrained to multi-format and no format/instruction-type dropdown is
   * shown.
   */
  isMultiformat: boolean;
}

/**
 * A set of filters for the Past Assessments component.
 */
export const PastAssessmentsFilters = ({
  areExamsEnabled,
  subjects,
  subjectGroups,
  isGroupAssessment,
  isMultiformat,
}: PastAssessmentsFiltersProps) => {
  const pastAssessmentFilters = useAppSelector(selectPastAssessmentFilters);
  const dispatch = useAppDispatch();

  const [startDateValue, endDateValue] = pastAssessmentFilters.dueDateRange ?? [
    null,
    null,
  ];

  const datePickerRangeValue: DatePickerValue<"range"> = [
    startDateValue != null ? reduxDateDeserialiser(startDateValue) : null,
    endDateValue != null ? reduxDateDeserialiser(endDateValue) : null,
  ];

  const selectedInstructionTypes = pastAssessmentFilters.instructionTypes ?? [];

  useEffect(() => {
    if (isMultiformat) {
      if (
        pastAssessmentFilters.assessmentType != null ||
        pastAssessmentFilters.taskFormat != null ||
        pastAssessmentFilters.instructionTypes != null
      ) {
        const nextFilters = { ...pastAssessmentFilters };
        delete nextFilters.assessmentType;
        delete nextFilters.taskFormat;
        delete nextFilters.instructionTypes;
        dispatch(updatePastAssessmentsFilter(nextFilters));
      }
      return;
    }

    if (isGroupAssessment) {
      if (
        pastAssessmentFilters.assessmentType != null ||
        pastAssessmentFilters.taskFormat != null
      ) {
        const nextFilters = { ...pastAssessmentFilters };
        delete nextFilters.assessmentType;
        delete nextFilters.taskFormat;
        dispatch(updatePastAssessmentsFilter(nextFilters));
      }
      return;
    }

    if (pastAssessmentFilters.instructionTypes != null) {
      const nextFilters = { ...pastAssessmentFilters };
      delete nextFilters.instructionTypes;
      dispatch(updatePastAssessmentsFilter(nextFilters));
    }
  }, [dispatch, isGroupAssessment, isMultiformat, pastAssessmentFilters]);

  // The subject-group filter persists in Redux across modal opens, but its
  // dropdown only renders when groups are available. Clear a selected group
  // that is no longer among the available ones (e.g. after reopening for a
  // different destination) so a hidden, stale filter can't silently empty the
  // list.
  useEffect(() => {
    const selected = pastAssessmentFilters.subjectGroup;
    if (
      selected != null &&
      !subjectGroups.some((group) => group.id === selected.id)
    ) {
      const nextFilters = { ...pastAssessmentFilters };
      delete nextFilters.subjectGroup;
      dispatch(updatePastAssessmentsFilter(nextFilters));
    }
  }, [dispatch, subjectGroups, pastAssessmentFilters]);

  const toggleInstructionType = (value: InstructionType) => {
    const next = selectedInstructionTypes.includes(value)
      ? selectedInstructionTypes.filter((v) => v !== value)
      : [...selectedInstructionTypes, value];
    dispatch(
      updatePastAssessmentsFilter({
        ...pastAssessmentFilters,
        instructionTypes: next,
      })
    );
  };

  const containerClassName = isGroupAssessment
    ? styles.groupFilterGrid
    : styles.filterContainer;

  return (
    <div className={containerClassName}>
      <div className={styles.dropdownsRow}>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <SelectButton className={styles.filterField}>
              {pastAssessmentFilters.subject?.name ?? "Subject"}
            </SelectButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            {subjects.map((subject) => (
              <DropdownMenu.Item
                className={styles.menuItem}
                key={subject.id}
                onSelect={() => {
                  dispatch(
                    updatePastAssessmentsFilter({
                      ...pastAssessmentFilters,
                      subject: subject,
                    })
                  );
                }}
              >
                <Text
                  key={`${subject.id}-child`}
                  kind="systemMd"
                  textAlign={"left"}
                >
                  {subject.name}
                </Text>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        {subjectGroups.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <SelectButton className={styles.filterField}>
                {pastAssessmentFilters.subjectGroup?.name ?? "Subject group"}
              </SelectButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {subjectGroups.map((subjectGroup) => (
                <DropdownMenu.Item
                  className={styles.menuItem}
                  key={subjectGroup.id}
                  onSelect={() => {
                    dispatch(
                      updatePastAssessmentsFilter({
                        ...pastAssessmentFilters,
                        subjectGroup: subjectGroup,
                      })
                    );
                  }}
                >
                  <Text
                    key={`${subjectGroup.id}-child`}
                    kind="systemMd"
                    textAlign={"left"}
                  >
                    {subjectGroup.name}
                  </Text>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
        {areExamsEnabled && !isGroupAssessment && !isMultiformat && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <SelectButton className={styles.filterField}>
                {assessmentTypeSelectorOptions.find(
                  (option) =>
                    option.value === pastAssessmentFilters.assessmentType
                )?.display ?? "Type"}
              </SelectButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {assessmentTypeSelectorOptions.map((option) => (
                <DropdownMenu.Item
                  className={styles.menuItem}
                  key={option.display}
                  onSelect={() => {
                    dispatch(
                      updatePastAssessmentsFilter({
                        ...pastAssessmentFilters,
                        assessmentType: option.value,
                      })
                    );
                  }}
                >
                  <Text kind="systemMd" textAlign={"left"}>
                    {option.display}
                  </Text>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
        {!isMultiformat && !isGroupAssessment && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <SelectButton className={styles.filterField}>
                {taskFormatSelectorOptions.find(
                  (option) => option.value === pastAssessmentFilters.taskFormat
                )?.display ?? "Format"}
              </SelectButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {taskFormatSelectorOptions.map((option) => (
                <DropdownMenu.Item
                  className={styles.menuItem}
                  key={option.display}
                  onSelect={() => {
                    dispatch(
                      updatePastAssessmentsFilter({
                        ...pastAssessmentFilters,
                        taskFormat: option.value,
                      })
                    );
                  }}
                >
                  <Text kind="systemMd" textAlign={"left"}>
                    {option.display}
                  </Text>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
        {!isMultiformat && isGroupAssessment && (
          <Combobox.Root>
            <Combobox.Trigger asChild>
              <SelectButton className={styles.filterField}>
                Instruction type
                {selectedInstructionTypes.length > 0 && (
                  <Counter active>{selectedInstructionTypes.length}</Counter>
                )}
              </SelectButton>
            </Combobox.Trigger>
            <Combobox.Content align="start">
              <Command.Root className={styles.compactCommandRoot}>
                <Command.List>
                  <Command.Group>
                    {instructionTypeSelectorOptions.map((option) => (
                      <Command.Item
                        key={option.value}
                        checkable
                        data-selected={
                          selectedInstructionTypes.includes(option.value)
                            ? "true"
                            : undefined
                        }
                        onSelect={() => toggleInstructionType(option.value)}
                      >
                        <Text kind="bodyMd" textAlign="left" color="black100">
                          {option.display}
                        </Text>
                      </Command.Item>
                    ))}
                  </Command.Group>
                </Command.List>
              </Command.Root>
            </Combobox.Content>
          </Combobox.Root>
        )}
      </div>
      {(() => {
        const dueDatePicker = (
          <DatePickerInput
            type="range"
            data-testid={"dueDateRangePicker"}
            allowSingleDateInRange
            inputClassName={styles.filterField}
            popoverProps={{
              withinPortal: false,
            }}
            placeholder={
              !isGroupAssessment &&
              areExamsEnabled &&
              pastAssessmentFilters.assessmentType === AssessmentType.Exam
                ? "Exam date range"
                : "Due date range"
            }
            value={datePickerRangeValue}
            onChange={(selectedValues: DatesRangeValue) => {
              dispatch(
                updatePastAssessmentsFilter({
                  ...pastAssessmentFilters,
                  dueDateRange: selectedValues.map((selectedValue) =>
                    selectedValue != null
                      ? getLocalDate(selectedValue?.toString()).getTime()
                      : null
                  ) as SerializedDateRangePickerValue,
                })
              );
            }}
          />
        );

        const searchInput = (
          <Input
            className={styles.filterField}
            placeholder="Assessment name"
            fullWidth
            value={pastAssessmentFilters.assessmentNameQuery ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              dispatch(
                updatePastAssessmentsFilter({
                  ...pastAssessmentFilters,
                  assessmentNameQuery: value,
                })
              );
            }}
            leftSection={<MagnifyingGlassIcon label="Search" size={22} />}
          />
        );

        return (
          <div className={styles.searchRow}>
            {dueDatePicker}
            {searchInput}
          </div>
        );
      })()}
    </div>
  );
};

type AssessmentTypeOption = {
  display: string;
  value: Exclude<IPastAssessmentFilters["assessmentType"], undefined>;
};

const assessmentTypeSelectorOptions = [
  { display: "All", value: "all" },
  { display: "Assignments", value: AssessmentType.Assignment },
  { display: "Exams", value: AssessmentType.Exam },
] satisfies AssessmentTypeOption[];

type InstructionTypeOption = {
  display: string;
  value: InstructionType;
};

type TaskFormatOption = {
  display: string;
  value: Exclude<IPastAssessmentFilters["taskFormat"], undefined>;
};

const instructionTypeSelectorOptions = [
  { display: "Group work", value: "group-work" },
  { display: "Individual work", value: "individual-work" },
  { display: "Classic", value: "cadmus-classic" },
] satisfies InstructionTypeOption[];

const taskFormatSelectorOptions = [
  { display: "All", value: "all" },
  { display: "Multi-format", value: TaskFormat.Multiformat },
  { display: "Classic", value: TaskFormat.Classic },
] satisfies TaskFormatOption[];
